import { AppState } from '@/store';
import { CustomModal } from '@components/tailwind';
import { CustomModalRef } from '@components/tailwind/CustomModal';
import usePartnerAssetGroups from '@features/partner/hooks/usePartnerAssetGroups';
import { selectAsset } from '@features/partner/redux/partner.actions';
import { PartnerActionTypes, TAsset, TAssetGroup } from '@types';
import classNames from 'classnames';
import { useMemo, useRef } from 'react';
import { BsChevronRight } from 'react-icons/bs';
import { FaChevronLeft } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useAppDispatch } from '@/store';
import Slider from 'react-slick';

const SLIDER_OPTIONS = {
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  variableWidth: true,
  infinite: false,
  centerMode: false,
  dots: true,
  responsive: [
    {
      breakpoint: 550,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        infinite: false,
        variableWidth: true,
      },
    },
  ],
};

const DesignsSlider = () => {
  const dispatch = useAppDispatch();
  const customModalRef = useRef<CustomModalRef>(null);
  const {
    partnerData: {
      partner: { galleryUrl, title },
      assetGroups,
      assets,
    },
    activeAsset,
  } = useSelector((state: AppState) => state.partner);
  const { activeGroup, selectGroup, rootGroup, level } = usePartnerAssetGroups({
    activeAsset,
    assets,
    assetGroups,
  });

  const onGroupClick = (group) => () => {
    selectGroup(group)();
    dispatch({ type: PartnerActionTypes.SET_ACTIVE_GROUP, payload: group });
    customModalRef.current.openModal();
  };

  const selectAssetHandler = (asset: TAsset) => () => {
    dispatch(selectAsset(asset));
    customModalRef.current.closeModal();
  };

  console.log('activeGroup', activeGroup);

  const modalTitle = useMemo(() => {
    if (activeGroup?.assetGroup?.groupName && activeGroup?.prevAssetGroup && level > 2) {
      return (
        <div
          className='tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-py-2 tw-font-bold'
          onClick={selectGroup(activeGroup.prevAssetGroup)}>
          <FaChevronLeft size={16} />
          <p className='tw-mb-0'>{activeGroup?.assetGroup?.groupName}</p>
        </div>
      );
    }

    return activeGroup?.assetGroup?.groupName || 'All Designs';
  }, [activeGroup, level]);

  return (
    <>
      <div className='tw-my-10 -tw-mx-5 tw-shadow-lg'>
        <div className='tw-mb-5 tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-7 md:tw-gap-3'>
          <img src={galleryUrl} alt={`${title} Design Gallery Logo`} className='tw-max-h-6' />
          <p className='tw-mb-0 tw-text-sm tw-font-bold'>DESIGN GALLERY</p>
        </div>

        <Slider {...SLIDER_OPTIONS} className=''>
          {rootGroup.groups.map((item) => (
            <div className='tw-flex tw-justify-center tw-px-2' key={item.id}>
              <BrowseDesignsByItem item={item} onClick={onGroupClick(item)} />
            </div>
          ))}
          <div className='tw-flex tw-justify-center tw-px-2'>
            <BrowseDesignsByItem
              item={{
                ...rootGroup.groups[0],
                groupName: 'Browse All Designs',
                previewUrl: galleryUrl,
              }}
              onClick={onGroupClick(null)}
              hasIcon
            />
          </div>
        </Slider>
      </div>

      <CustomModal ref={customModalRef} title={modalTitle} removeBodyPadding>
        {(activeGroup.assets || activeGroup.groups) &&
          (activeGroup.assets.length > 0 || activeGroup.groups.length > 0) && (
            <div className='tw-flex tw-max-h-[400px] tw-flex-col tw-gap-3 tw-overflow-y-scroll tw-py-4'>
              {activeGroup.groups.map((group) => (
                <div
                  key={group.id}
                  onClick={selectGroup(group)}
                  className='tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-transition tw-duration-200 hover:tw-bg-neutral-200'>
                  <div className='tw-flex tw-items-center tw-gap-3'>
                    {group?.previewUrl && (
                      <img src={group.previewUrl} alt={group.groupName} className='tw-h-10 tw-w-10' />
                    )}
                    <p className='tw-mb-0 tw-text-sm tw-font-normal'>{group.groupName}</p>
                  </div>
                  <BsChevronRight size={14} />
                </div>
              ))}
              {activeGroup.assets.map((item) => (
                <div
                  key={item.id}
                  onClick={selectAssetHandler(item)}
                  className={classNames(
                    'tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-px-4 tw-transition tw-duration-200 hover:tw-bg-neutral-200',
                    {
                      'tw-bg-neutral-200': activeAsset?.id === item.id,
                    }
                  )}>
                  <img src={item.fileUrl} alt={item.assetName} className='tw-h-10 tw-w-10' />
                  <p className='tw-mb-0 tw-text-sm tw-font-normal'>{item.assetName}</p>
                </div>
              ))}
            </div>
          )}
      </CustomModal>
    </>
  );
};

export default DesignsSlider;

type BrowseDesignsByItemProps = {
  item: TAssetGroup;
  hasIcon?: boolean;
  className?: string;
  onClick: () => void;
};

const BrowseDesignsByItem = ({
  item,
  hasIcon = false,
  className = '',
  onClick,
}: BrowseDesignsByItemProps) => {
  return (
    <div
      onClick={onClick}
      className={classNames(
        'tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-button tw-border-gray-300 tw-py-2 tw-px-5 tw-text-body tw-no-underline tw-transition tw-duration-300 tw-ease-in-out hover:tw-text-body md:tw-border md:hover:tw-bg-gray-200',
        className
      )}>
      {item.previewUrl && (
        <div className='tw-inline-flex tw-h-[45px] tw-w-[55px] tw-items-center tw-justify-center'>
          <img src={item.previewUrl} alt={item.groupName} className='tw-max-h-full tw-max-w-full' />
        </div>
      )}
      <div className='tw-inline-flex tw-w-full tw-items-center tw-justify-between tw-gap-3'>
        <div>
          <p className='tw-mb-0 tw-text-xs tw-font-bold'>{item.groupName}</p>
        </div>
        {hasIcon && <BsChevronRight size={12} />}
      </div>
    </div>
  );
};
